// Contracts — kids MOD_PLANS ("expectations", 2026-06-30). A moderator's own
// bottom-up plan for how many shoots they expect to do today / this week.
// Shapes snapped from migrations/0003_finance_plans_referral.sql:
//   mod_plans(moderator_id TEXT, date TEXT 'YYYY-MM-DD', planned_today INTEGER,
//             planned_week INTEGER, answered_at INTEGER, PRIMARY KEY(moderator_id,date))
//
// SEMANTICS (locked by Tim): this is NOT the pace-target "plan/day" (goals ÷ N
// moderators — that's handlers_goal.ts / PaceWidget, top-down, untouched). This
// is the moderator's SELF-declared expectation (bottom-up). In V1 the moderator
// enters it in the app; later a TG bot writes the same mod_plans rows.
//
// Scope (mirrors the admin cabinet, finance.ts): manager = all offices (or
// ?office=); admin = own office only; the aggregate lists per-moderator numbers.
import { z } from 'zod';

// Whole non-negative integers. today capped at 50 shoots, week at 500 — sane
// upper bounds so a fat-finger can't poison the aggregate. Nullable: a moderator
// may set only one of the two (e.g. today but not the week).
const PlannedToday = z.number().int().min(0).max(50);
const PlannedWeek = z.number().int().min(0).max(500);

// ── GET /api/kids/plans/me ───────────────────────────────────────────────────
// The calling moderator's own expectation for TODAY (office-local date). null
// when they haven't answered yet.
export const KidsMyPlanResSchema = z.object({
  date: z.string(), // YYYY-MM-DD (office-local today)
  plannedToday: z.number().int().nullable(),
  plannedWeek: z.number().int().nullable(),
});
export type KidsMyPlanRes = z.infer<typeof KidsMyPlanResSchema>;

// ── PUT /api/kids/plans/me ───────────────────────────────────────────────────
// Upsert the caller's expectation for today. Both fields optional (patch-ish):
// an omitted field is left unchanged; an explicit null clears it. At least one
// of the two must be present (an empty body is a no-op the handler rejects).
export const KidsMyPlanPutReqSchema = z
  .object({
    plannedToday: PlannedToday.nullable().optional(),
    plannedWeek: PlannedWeek.nullable().optional(),
  })
  .refine(
    (o) => o.plannedToday !== undefined || o.plannedWeek !== undefined,
    'at least one of plannedToday / plannedWeek is required',
  );
export type KidsMyPlanPutReq = z.infer<typeof KidsMyPlanPutReqSchema>;

// PUT echoes the current (post-upsert) values, same shape as GET.
export const KidsMyPlanPutResSchema = KidsMyPlanResSchema;
export type KidsMyPlanPutRes = z.infer<typeof KidsMyPlanPutResSchema>;

// ── GET /api/kids/plans?office=&date= ────────────────────────────────────────
// Aggregate for manager/admin: per-moderator expectations for a date (default
// today) + totals. manager: all offices (or ?office=); admin: own office only
// (a foreign ?office= is clamped/denied server-side, like the finance reads).
export const KidsPlansQuerySchema = z.object({
  office: z.string().trim().max(60).optional(), // manager only; admin forced to own
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD')
    .optional(),
});
export type KidsPlansQuery = z.infer<typeof KidsPlansQuerySchema>;

export const KidsPlanRowSchema = z.object({
  moderatorId: z.string(),
  name: z.string(),
  pid: z.string().nullable(),
  office: z.string().nullable(),
  plannedToday: z.number().int().nullable(),
  plannedWeek: z.number().int().nullable(),
});
export type KidsPlanRow = z.infer<typeof KidsPlanRowSchema>;

export const KidsPlansTotalsSchema = z.object({
  today: z.number().int(), // Σ plannedToday over the rows (nulls as 0)
  week: z.number().int(), // Σ plannedWeek over the rows (nulls as 0)
});
export type KidsPlansTotals = z.infer<typeof KidsPlansTotalsSchema>;

export const KidsPlansResSchema = z.object({
  date: z.string(), // the resolved date the aggregate is for
  // Mon..Sun bounds of `date`'s week — the window the per-row plannedWeek is
  // resolved for (see handlers_plans.ts weekOf: weekly answers carry across the
  // week; a Sat/Sun row belongs to the NEXT week — the TG bot asks about the
  // next week on Saturday).
  week: z.object({
    start: z.string(), // Monday, YYYY-MM-DD
    end: z.string(), // Sunday, YYYY-MM-DD
  }),
  plans: z.array(KidsPlanRowSchema),
  totals: KidsPlansTotalsSchema,
});
export type KidsPlansRes = z.infer<typeof KidsPlansResSchema>;
